import { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, Plus, X, Save, RefreshCw, Trash2, Radio } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_NUMS = [1, 2, 3, 4, 5, 6, 7]; // AzuraCast uses 1=Mon ... 7=Sun
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const COLORS = [
  "bg-purple-500/40 border-purple-500/60",
  "bg-blue-500/40 border-blue-500/60",
  "bg-green-500/40 border-green-500/60",
  "bg-orange-500/40 border-orange-500/60",
  "bg-red-500/40 border-red-500/60",
  "bg-teal-500/40 border-teal-500/60",
  "bg-pink-500/40 border-pink-500/60",
  "bg-yellow-500/40 border-yellow-500/60",
  "bg-indigo-500/40 border-indigo-500/60",
  "bg-cyan-500/40 border-cyan-500/60",
];

const formatTime = (hhmm) => {
  if (hhmm === undefined || hhmm === null) return "";
  const h = Math.floor(hhmm / 100);
  const m = hhmm % 100;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export const ScheduleEditor = ({ authToken, currentUser }) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    playlist_id: "", start_hour: 0, start_min: 0, end_hour: 1, end_min: 0, days: [1, 2, 3, 4, 5, 6, 7],
  });

  const fetchPlaylists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setPlaylists(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => {
    if (currentUser?.azuracast_station_id) fetchPlaylists();
    else setLoading(false);
  }, [fetchPlaylists, currentUser]);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  // Collect all schedule blocks across all playlists
  const scheduleBlocks = [];
  playlists.forEach((p, idx) => {
    (p.schedule_items || []).forEach((item) => {
      scheduleBlocks.push({
        playlistId: p.id,
        playlistName: p.name,
        scheduleId: item.id,
        startTime: item.start_time,
        endTime: item.end_time,
        days: item.days || [],
        colorClass: COLORS[idx % COLORS.length],
      });
    });
  });

  // Map blocks to grid positions
  const getBlocksForCell = (dayNum, hour) => {
    return scheduleBlocks.filter((b) => {
      if (!b.days.includes(dayNum)) return false;
      const startH = Math.floor(b.startTime / 100);
      const endH = Math.floor(b.endTime / 100);
      const endM = b.endTime % 100;
      const effectiveEnd = endM > 0 ? endH + 1 : endH;
      if (b.endTime <= b.startTime) {
        // Wraps midnight
        return hour >= startH || hour < effectiveEnd;
      }
      return hour >= startH && hour < effectiveEnd;
    });
  };

  const handleAddSchedule = async () => {
    if (!addForm.playlist_id) { flash("Select a playlist", "error"); return; }
    const playlistId = parseInt(addForm.playlist_id);
    const playlist = playlists.find((p) => p.id === playlistId);
    if (!playlist) return;

    const newItem = {
      start_time: addForm.start_hour * 100 + addForm.start_min,
      end_time: addForm.end_hour * 100 + addForm.end_min,
      days: addForm.days,
      loop_once: false,
    };
    const existingItems = (playlist.schedule_items || []).map((s) => ({
      start_time: s.start_time, end_time: s.end_time, days: s.days, loop_once: s.loop_once,
    }));
    existingItems.push(newItem);

    setSaving(playlistId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists/${playlistId}/schedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ schedule_items: existingItems }),
      });
      if (res.ok) {
        flash(`Schedule added to "${playlist.name}"`);
        setShowAddForm(false);
        setAddForm({ playlist_id: "", start_hour: 0, start_min: 0, end_hour: 1, end_min: 0, days: [1, 2, 3, 4, 5, 6, 7] });
        fetchPlaylists();
      } else {
        const d = await res.json();
        flash(d.detail || "Failed", "error");
      }
    } catch (e) { flash(e.message, "error"); }
    finally { setSaving(null); }
  };

  const handleRemoveSchedule = async (playlistId, scheduleId) => {
    const playlist = playlists.find((p) => p.id === playlistId);
    if (!playlist) return;
    const updatedItems = (playlist.schedule_items || [])
      .filter((s) => s.id !== scheduleId)
      .map((s) => ({
        start_time: s.start_time, end_time: s.end_time, days: s.days, loop_once: s.loop_once,
      }));

    setSaving(playlistId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/playlists/${playlistId}/schedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ schedule_items: updatedItems }),
      });
      if (res.ok) { flash("Schedule block removed"); fetchPlaylists(); }
    } catch (e) { flash(e.message, "error"); }
    finally { setSaving(null); }
  };

  const toggleDay = (day) => {
    setAddForm((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day].sort(),
    }));
  };

  if (!currentUser?.azuracast_station_id) {
    return (
      <div className="text-center py-12">
        <Radio className="w-12 h-12 text-white/20 mx-auto mb-3" />
        <p className="text-white/50">Connect your station to manage schedules</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Weekly Schedule</h3>
          <p className="text-white/40 text-sm">{scheduleBlocks.length} schedule blocks across {playlists.filter(p => (p.schedule_items || []).length > 0).length} playlists</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchPlaylists} className="p-2 text-white/50 hover:text-white transition" data-testid="schedule-refresh">
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition"
            data-testid="schedule-add-btn"
          >
            <Plus className="w-4 h-4" /> Add Schedule Block
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`px-4 py-2 rounded-xl text-sm ${
          message.type === "error" ? "bg-red-500/20 text-red-300" : "bg-green-500/20 text-green-300"
        }`}>{message.text}</div>
      )}

      {/* Add Schedule Form */}
      {showAddForm && (
        <div className="bg-white/5 border border-purple-500/30 rounded-2xl p-5 space-y-4" data-testid="schedule-add-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold">New Schedule Block</h4>
            <button onClick={() => setShowAddForm(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Playlist</label>
              <select
                value={addForm.playlist_id}
                onChange={(e) => setAddForm({ ...addForm, playlist_id: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
                data-testid="schedule-playlist-select"
              >
                <option value="">Select playlist...</option>
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.num_songs} tracks)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Start Time</label>
              <div className="flex gap-2">
                <select value={addForm.start_hour} onChange={(e) => setAddForm({ ...addForm, start_hour: parseInt(e.target.value) })}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50">
                  {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
                </select>
                <select value={addForm.start_min} onChange={(e) => setAddForm({ ...addForm, start_min: parseInt(e.target.value) })}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50">
                  {Array.from({ length: 60 }, (_, i) => i).map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">End Time</label>
              <div className="flex gap-2">
                <select value={addForm.end_hour} onChange={(e) => setAddForm({ ...addForm, end_hour: parseInt(e.target.value) })}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50">
                  {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
                </select>
                <select value={addForm.end_min} onChange={(e) => setAddForm({ ...addForm, end_min: parseInt(e.target.value) })}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50">
                  {Array.from({ length: 60 }, (_, i) => i).map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Days */}
          <div>
            <label className="block text-white/60 text-xs mb-1.5">Active Days</label>
            <div className="flex gap-2">
              {DAYS.map((day, idx) => (
                <button
                  key={day} type="button"
                  onClick={() => toggleDay(DAY_NUMS[idx])}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition ${
                    addForm.days.includes(DAY_NUMS[idx])
                      ? "bg-purple-500/30 border border-purple-500/50 text-purple-300"
                      : "bg-white/5 border border-white/10 text-white/30 hover:text-white"
                  }`}
                  data-testid={`schedule-day-${day.toLowerCase()}`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleAddSchedule} disabled={!!saving}
            className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition"
            data-testid="schedule-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Add to Schedule"}
          </button>
        </div>
      )}

      {/* Weekly Grid */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="schedule-grid">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Header */}
            <div className="grid grid-cols-8 border-b border-white/10">
              <div className="p-3 text-white/40 text-xs"></div>
              {DAYS.map((day) => (
                <div key={day} className="p-3 text-center text-white/60 text-xs font-semibold uppercase">{day}</div>
              ))}
            </div>
            {/* Hour rows */}
            {HOURS.map((hour) => (
              <div key={hour} className="grid grid-cols-8 border-b border-white/[0.03]">
                <div className="p-2 text-white/30 text-[11px] text-right pr-3 border-r border-white/5">
                  {hour === 0 ? "12 AM" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`}
                </div>
                {DAY_NUMS.map((dayNum) => {
                  const blocks = getBlocksForCell(dayNum, hour);
                  return (
                    <div key={dayNum} className="p-0.5 min-h-[32px] border-r border-white/[0.03]">
                      {blocks.map((b, i) => (
                        <div
                          key={`${b.playlistId}-${b.scheduleId}-${i}`}
                          className={`${b.colorClass} border rounded px-1.5 py-0.5 text-[9px] text-white truncate mb-0.5 group relative cursor-default`}
                          title={`${b.playlistName}: ${formatTime(b.startTime)} - ${formatTime(b.endTime)}`}
                        >
                          {b.playlistName}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveSchedule(b.playlistId, b.scheduleId); }}
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full items-center justify-center text-white hidden group-hover:flex"
                            title="Remove"
                          >
                            <X className="w-2 h-2" />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      {scheduleBlocks.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {playlists
            .filter((p) => (p.schedule_items || []).length > 0)
            .map((p, idx) => (
              <div key={p.id} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${COLORS[playlists.indexOf(p) % COLORS.length].split(" ")[0]}`} />
                <span className="text-white/60 text-xs">{p.name}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
